/**
 * Super-admin API methods for admin and super-admin management.
 * Used by super_admin role to manage the admin hierarchy.
 */

import { api } from './client.js';

// =============================================================================
// Admin Management
// =============================================================================

/**
 * List all admins.
 *
 * @returns {Promise<{admins: Array<Object>}>} List of admin objects
 * @throws {ApiError} If unauthorized
 */
export async function listAdmins() {
  return api.get('/super-admin/admins');
}

/**
 * Get a specific admin by ID.
 *
 * @param {string} adminId - The admin ID to retrieve
 * @returns {Promise<Object>} Admin object with details
 * @throws {ApiError} If admin not found or unauthorized
 */
export async function getAdmin(adminId) {
  const path = `/super-admin/admins/${encodeURIComponent(adminId)}`;
  return api.get(path);
}

/**
 * Create a new admin.
 *
 * @param {string} partyId - Unique identifier for the new admin
 * @returns {Promise<Object>} Created admin object with API key
 * @throws {ApiError} If admin ID already exists or unauthorized
 */
export async function createAdmin(partyId) {
  return api.post('/super-admin/admins', { party_id: partyId });
}

/**
 * Delete an admin.
 *
 * @param {string} adminId - The admin ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 * @throws {ApiError} If admin not found or unauthorized
 */
export async function deleteAdmin(adminId) {
  const path = `/super-admin/admins/${encodeURIComponent(adminId)}`;
  return api.delete(path);
}

/**
 * List all API keys for an admin.
 *
 * @param {string} adminId - The admin ID
 * @returns {Promise<{keys: Array<Object>}>} List of API key objects
 * @throws {ApiError} If admin not found or unauthorized
 */
export async function listAdminKeys(adminId) {
  const path = `/super-admin/admins/${encodeURIComponent(adminId)}/keys`;
  return api.get(path);
}

/**
 * Create a new API key for an admin.
 *
 * @param {string} adminId - The admin ID
 * @returns {Promise<Object>} Created key object with plain API key
 * @throws {ApiError} If admin not found or unauthorized
 */
export async function createAdminKey(adminId) {
  const path = `/super-admin/admins/${encodeURIComponent(adminId)}/keys`;
  return api.post(path, {});
}

/**
 * Revoke an API key for an admin.
 *
 * @param {string} adminId - The admin ID
 * @param {string} keyId - The key ID to revoke
 * @returns {Promise<Object>} Revocation confirmation
 * @throws {ApiError} If admin or key not found, or unauthorized
 */
export async function revokeAdminKey(adminId, keyId) {
  const path = `/super-admin/admins/${encodeURIComponent(adminId)}/keys/${encodeURIComponent(keyId)}`;
  return api.delete(path);
}

// =============================================================================
// Super-Admin Management
// =============================================================================

/**
 * List all super-admins.
 *
 * @returns {Promise<{super_admins: Array<Object>}>} List of super-admin objects
 * @throws {ApiError} If unauthorized
 */
export async function listSuperAdmins() {
  return api.get('/super-admin/super-admins');
}

/**
 * Get a specific super-admin by ID.
 *
 * @param {string} superAdminId - The super-admin ID to retrieve
 * @returns {Promise<Object>} Super-admin object with details
 * @throws {ApiError} If super-admin not found or unauthorized
 */
export async function getSuperAdmin(superAdminId) {
  const path = `/super-admin/super-admins/${encodeURIComponent(superAdminId)}`;
  return api.get(path);
}

/**
 * Create a new super-admin.
 *
 * @param {string} partyId - Unique identifier for the new super-admin
 * @returns {Promise<Object>} Created super-admin object with API key
 * @throws {ApiError} If super-admin ID already exists or unauthorized
 */
export async function createSuperAdmin(partyId) {
  return api.post('/super-admin/super-admins', { party_id: partyId });
}

/**
 * Delete a super-admin.
 *
 * @param {string} superAdminId - The super-admin ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 * @throws {ApiError} If super-admin not found or unauthorized
 */
export async function deleteSuperAdmin(superAdminId) {
  const path = `/super-admin/super-admins/${encodeURIComponent(superAdminId)}`;
  return api.delete(path);
}

/**
 * List all API keys for a super-admin.
 *
 * @param {string} superAdminId - The super-admin ID
 * @returns {Promise<{keys: Array<Object>}>} List of API key objects
 * @throws {ApiError} If super-admin not found or unauthorized
 */
export async function listSuperAdminKeys(superAdminId) {
  const path = `/super-admin/super-admins/${encodeURIComponent(superAdminId)}/keys`;
  return api.get(path);
}

/**
 * Create a new API key for a super-admin.
 *
 * @param {string} superAdminId - The super-admin ID
 * @returns {Promise<Object>} Created key object with plain API key
 * @throws {ApiError} If super-admin not found or unauthorized
 */
export async function createSuperAdminKey(superAdminId) {
  const path = `/super-admin/super-admins/${encodeURIComponent(superAdminId)}/keys`;
  return api.post(path, {});
}

/**
 * Revoke an API key for a super-admin.
 *
 * @param {string} superAdminId - The super-admin ID
 * @param {string} keyId - The key ID to revoke
 * @returns {Promise<Object>} Revocation confirmation
 * @throws {ApiError} If super-admin or key not found, or unauthorized
 */
export async function revokeSuperAdminKey(superAdminId, keyId) {
  const path = `/super-admin/super-admins/${encodeURIComponent(superAdminId)}/keys/${encodeURIComponent(keyId)}`;
  return api.delete(path);
}
